import * as React from 'react'
import { useDispatch } from 'react-redux'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { query } from '../common/api'
import { setUser } from './authActions'
import { currentUserSelector } from './authSelectors'
import { useSelector } from 'react-redux'
import Modal from '../components/Modal'
import Alert from '../components/Alert'
import { currentRevision } from './terms'

import LogoIcon from '../icons/logo.svg'
import FileIcon from '../icons/file.svg'
import ArrowRightIcon from '../icons/arrow_right.svg'

// From the revision list rather than repeated here, so the documents shown
// and the documents the re-prompt is keyed to cannot drift apart.
const { eulaUuid: EULA_UUID, privacyUuid: PRIVACY_UUID } = currentRevision()

const GENERIC_ERROR =
  'We could not save your acceptance. Please try again, or contact support if this keeps happening.'

const WELCOME_DESCRIPTION =
  'Before you can access ForeSight, please review our End User License ' +
  'Agreement and Privacy Policy. These documents explain how the application ' +
  'works, your rights, and how we handle your data.'

// Deliberately silent on what changed: the release announcement carries that,
// and this has to read correctly for every future revision too.
const UPDATED_DESCRIPTION =
  'Our End User License Agreement and Privacy Policy have been updated. ' +
  'Please review them and accept to continue using ForeSight. These ' +
  'documents explain how the application works, your rights, and how we ' +
  'handle your data.'

const ACCEPT_TERMS_MUTATION = `
  mutation {
    acceptTerms {
      result {
        id
        termsAcceptedAt
      }
      errors
    }
  }
`

interface IPolicyModalProps {
  title: string
  policyUuid: string
  onClose: () => void
}

const PolicyModal: FunctionComponent<IPolicyModalProps> = ({
  title,
  policyUuid,
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const existingScript = document.getElementById('termly-jssdk')
    if (existingScript) existingScript.remove()

    const script = document.createElement('script')
    script.id = 'termly-jssdk'
    script.src = 'https://app.termly.io/embed-policy.min.js'
    script.type = 'text/javascript'
    document.body.appendChild(script)

    return () => {
      const s = document.getElementById('termly-jssdk')
      if (s) s.remove()
    }
  }, [policyUuid])

  return (
    <Modal title={title} onClose={onClose}>
      <div ref={containerRef}>
        <div
          // @ts-ignore: name attribute not in HTMLAttributes but required by Termly
          name="termly-embed"
          data-id={policyUuid}
        />
      </div>
    </Modal>
  )
}

const TermsAndConditions: FunctionComponent = () => {
  const dispatch = useDispatch()
  const user = useSelector(currentUserSelector)

  const [accepted, setAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [policyModal, setPolicyModal] = useState<{
    title: string
    uuid: string
  } | null>(null)

  const openPolicyModal = useCallback(
    (modal: { title: string; uuid: string }) => {
      window.history.pushState({ policyModal: true }, '')
      setPolicyModal(modal)
    },
    [],
  )

  const closePolicyModal = useCallback(() => {
    setPolicyModal(null)
  }, [])

  useEffect(() => {
    if (!policyModal) return
    const handlePopState = () => setPolicyModal(null)
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [policyModal])

  const onSubmit = useCallback(async () => {
    if (!accepted || submitting) return

    setSubmitting(true)
    setError(null)
    // On success this component unmounts, so only failures reset the flag
    try {
      const data = await query(ACCEPT_TERMS_MUTATION)
      const updatedUser = data?.acceptTerms?.result
      if (updatedUser) {
        dispatch(setUser({ ...user, ...updatedUser }))
        return
      }
      setError(GENERIC_ERROR)
      setSubmitting(false)
    } catch {
      setError(GENERIC_ERROR)
      setSubmitting(false)
    }
  }, [accepted, submitting, user, dispatch])

  // Someone who has accepted before is being asked again because the terms
  // changed, not welcomed for the first time. Deliberately generic about what
  // changed: the release announcement carries that, and this copy has to hold
  // for every future revision.
  const returning = Boolean(user?.termsAcceptedAt)

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {error && <Alert>{error}</Alert>}

        <div style={styles.header}>
          <LogoIcon style={styles.logo} />
          <div style={styles.title}>
            {returning ? "We've updated our terms" : 'Welcome to ForeSight'}
          </div>
          <div style={styles.subtitle}>
            {returning
              ? 'Please review and accept to continue'
              : 'Review and accept to get started'}
          </div>
        </div>

        <p style={styles.description}>
          {returning ? UPDATED_DESCRIPTION : WELCOME_DESCRIPTION}
        </p>

        <div style={styles.documentsCard}>
          <button
            style={styles.documentRow}
            onClick={() =>
              openPolicyModal({
                title: 'End User License Agreement',
                uuid: EULA_UUID,
              })
            }
          >
            <FileIcon style={styles.documentIcon} />
            <div style={styles.documentText}>
              <span style={styles.documentTitle}>
                End User License Agreement
              </span>
              <span style={styles.documentSubtitle}>
                Click to review full document
              </span>
            </div>
            <ArrowRightIcon style={styles.arrow} />
          </button>
          <div style={styles.divider} />
          <button
            style={styles.documentRow}
            onClick={() =>
              openPolicyModal({ title: 'Privacy Policy', uuid: PRIVACY_UUID })
            }
          >
            <FileIcon style={styles.documentIcon} />
            <div style={styles.documentText}>
              <span style={styles.documentTitle}>Privacy Policy</span>
              <span style={styles.documentSubtitle}>
                Click to review full document
              </span>
            </div>
            <ArrowRightIcon style={styles.arrow} />
          </button>
        </div>

        <label style={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            style={styles.checkbox}
          />
          <span style={styles.checkboxLabel}>
            I have read and agree to the{' '}
            <button
              style={styles.inlineLink}
              onClick={(e) => {
                e.preventDefault()
                openPolicyModal({
                  title: 'End User License Agreement',
                  uuid: EULA_UUID,
                })
              }}
            >
              End User License Agreement
            </button>{' '}
            and acknowledge that I have read the{' '}
            <button
              style={styles.inlineLink}
              onClick={(e) => {
                e.preventDefault()
                openPolicyModal({ title: 'Privacy Policy', uuid: PRIVACY_UUID })
              }}
            >
              Privacy Policy
            </button>
            .
          </span>
        </label>

        <button
          className="button -primary -block"
          style={styles.submitButton}
          disabled={!accepted || submitting}
          onClick={onSubmit}
        >
          {returning ? 'Continue' : 'Get Started'}
        </button>
      </div>

      {policyModal && (
        <PolicyModal
          title={policyModal.title}
          policyUuid={policyModal.uuid}
          onClose={closePolicyModal}
        />
      )}
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffffff',
    padding: '45px 20px',
  },
  card: {
    width: '100%',
    maxWidth: 518,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  },
  logo: {
    width: 55,
    height: 55,
    marginBottom: 10,
  },
  title: {
    fontSize: 19,
    fontWeight: 500,
    color: 'var(--primary-text)',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: 'var(--secondary-text)',
    textAlign: 'center',
  },
  description: {
    fontSize: 14,
    color: 'var(--grey-again)',
    lineHeight: '20px',
    margin: '8px 0',
  },
  documentsCard: {
    border: '1px solid rgba(52, 82, 96, 0.12)',
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  documentRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: 16,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    gap: 16,
  },
  documentIcon: {
    width: 24,
    height: 24,
    color: 'var(--secondary-text)',
    flexShrink: 0,
  },
  documentText: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  documentTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: 'var(--primary-text)',
  },
  documentSubtitle: {
    fontSize: 13,
    color: 'var(--secondary-text)',
  },
  arrow: {
    width: 16,
    height: 16,
    color: 'var(--secondary-text)',
    flexShrink: 0,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(52, 82, 96, 0.12)',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '8px 0',
    cursor: 'pointer',
  },
  checkbox: {
    marginTop: 3,
    flexShrink: 0,
    accentColor: 'var(--primary-blue)',
    width: 16,
    height: 16,
  },
  checkboxLabel: {
    fontSize: 14,
    color: '#252525',
    lineHeight: '19px',
    letterSpacing: '0.4px',
  },
  inlineLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: 'var(--primary-blue)',
    fontSize: 14,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  submitButton: {
    height: 44,
    marginTop: 8,
    border: 'none',
  },
}

export default TermsAndConditions
